import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useProducts } from '../../hooks/useProducts'
import FilterPanel from '../ui/FilterPanel'
import ProductCard from '../product/ProductCard'
import { SkeletonGrid } from '../ui/Skeleton'
import { t } from '../../i18n/translations'
import { productApi, UserLocation } from '../../services/api'
import { Search, SlidersHorizontal, MapPin, X, Filter } from 'lucide-react'
import PageLayout, { AvatarButton } from '../layout/PageLayout'
import { useUserAvatar } from '../../hooks/useUserAvatar'

export default function Products() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tg = typeof window !== 'undefined' ? window.Telegram?.WebApp : null
  const user = tg?.initDataUnsafe?.user || null
  const avatarUrl = useUserAvatar()

  const {
    products,
    loading: productsLoading,
    error: productsError,
    searchProducts
  } = useProducts()

  // selectedTagIds holds selected CATEGORY ids (a category + its whole subtree).
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])
  const [selectedAttrs, setSelectedAttrs] = useState<Record<string, any>>({})
  const [filterPanelOpen, setFilterPanelOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [selectedCountry, setSelectedCountry] = useState<string | null>(null)
  const [selectedCity, setSelectedCity] = useState<string | null>(null)
  const [userLocation, setUserLocation] = useState<UserLocation | null | undefined>(undefined)

  // Handle search with debouncing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery || selectedTagIds.length > 0 || selectedCity) {
        searchProducts({
          query: searchQuery || undefined,
          category_ids: selectedTagIds.length > 0 ? selectedTagIds.join(',') : undefined,
          attrs: Object.keys(selectedAttrs).length > 0 ? selectedAttrs : undefined,
          country_code: selectedCountry || undefined,
          city_name: selectedCity || undefined
        })
      }
    }, 500)

    return () => clearTimeout(timer)
  }, [searchQuery])

  // Fetch user location on mount
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const location = await productApi.getUserLocation()
        setUserLocation(location)
      } catch (error) {
        console.error('Failed to fetch user location:', error)
        setUserLocation(null)
      }
    }

    fetchUserLocation()
  }, [])

  // Initialize filters from URL and load products
  useEffect(() => {
    if (userLocation === undefined) return

    const catIds = searchParams.get('categories')
    const country = searchParams.get('country')
    const city = searchParams.get('city')

    if (catIds) {
      setSelectedTagIds(catIds.split(','))
    }

    if (country || city) {
      setSelectedCountry(country)
      setSelectedCity(city)
    }

    searchProducts({
      category_ids: catIds || undefined,
      country_code: country || undefined,
      city_name: city || undefined,
      query: undefined
    })
  }, [userLocation])

  useEffect(() => {
    if (!tg) return

    // Hide back button on main page
    try {
      tg.BackButton?.hide()
    } catch (error) {
      console.warn('Failed to hide back button:', error)
    }

    const lastStore = sessionStorage.getItem('lastVisitedStore')
    const storeName = sessionStorage.getItem('storeOwnerName')

    if (lastStore && storeName && tg.MainButton) {
      try {
        tg.MainButton?.setText(`↩ Back to ${storeName}'s Store`)
        tg.MainButton?.show()

        const handleBackToStore = () => {
          navigate(lastStore)
        }

        tg.MainButton?.onClick(handleBackToStore)

        return () => {
          tg.MainButton?.offClick(handleBackToStore)
          tg.MainButton?.hide()
        }
      } catch (error) {
        console.warn('Failed to setup main button:', error)
      }
    } else {
      try {
        tg.MainButton?.hide()
      } catch (error) {
        console.warn('Failed to hide main button:', error)
      }
    }
  }, [tg, navigate])

  // Handle location filter changes
  const handleLocationChange = (country: string | null, city: string | null) => {
    setSelectedCountry(country)
    setSelectedCity(city)

    if (country) {
      searchParams.set('country', country)
    } else {
      searchParams.delete('country')
    }

    if (city) {
      searchParams.set('city', city)
    } else {
      searchParams.delete('city')
    }

    setSearchParams(searchParams)

    searchProducts({
      category_ids: selectedTagIds.length > 0 ? selectedTagIds.join(',') : undefined,
      attrs: Object.keys(selectedAttrs).length > 0 ? selectedAttrs : undefined,
      country_code: country || undefined,
      city_name: city || undefined
    })
  }

  // Handle category + attribute filter application from the FilterPanel
  const handleApplyFilters = (categoryIds: string[], attrs: Record<string, any> = {}) => {
    setSelectedTagIds(categoryIds)
    setSelectedAttrs(attrs)

    if (categoryIds.length > 0) {
      searchParams.set('categories', categoryIds.join(','))
    } else {
      searchParams.delete('categories')
    }
    setSearchParams(searchParams)

    searchProducts({
      category_ids: categoryIds.length > 0 ? categoryIds.join(',') : undefined,
      attrs: Object.keys(attrs).length > 0 ? attrs : undefined,
      country_code: selectedCountry || undefined,
      city_name: selectedCity || undefined
    })
  }

  // Combined clear-all that resets both category filters AND location
  // in a single atomic state update to avoid race conditions where
  // handleLocationChange reads stale selectedTagIds from closure.
  const clearAllFilters = () => {
    setSelectedTagIds([])
    setSelectedAttrs({})
    setSelectedCountry(null)
    setSelectedCity(null)

    searchParams.delete('categories')
    searchParams.delete('country')
    searchParams.delete('city')
    setSearchParams(searchParams)

    searchProducts({})
  }

  const filteredProducts = products
  const activeFilterCount = selectedTagIds.length + Object.keys(selectedAttrs).length

  // Top header with brand, search bar, filters and avatar
  const topHeader = (
    <div className="px-4 pt-1 pb-3 bg-gradient-to-b from-blue-500 via-blue-600 to-blue-500">
      <div className="max-w-md mx-auto">
        {/* Brand row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <span className="text-white text-lg">⚡</span>
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-tight">Teleshop</h1>
              <p className="text-blue-200 text-[11px] leading-tight">Buy & sell in Telegram</p>
            </div>
          </div>
          {/* Avatar button to go to profile */}
          <AvatarButton
            imageUrl={avatarUrl}
            initials={user?.first_name?.charAt(0) || user?.username?.charAt(0) || '?'}
            onClick={() => navigate('/profile')}
            size="md"
          />
        </div>

        {/* Search row */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search products..."
              className="w-full bg-white/15 backdrop-blur-sm text-white rounded-2xl
                px-3 py-2.5 pl-10 text-sm placeholder:text-blue-200/70
                border border-white/10 focus:outline-none focus:ring-2 focus:ring-white/30
                focus:border-transparent transition-all"
            />
          </div>
          <button
            onClick={() => setFilterPanelOpen(!filterPanelOpen)}
            className={`p-2.5 rounded-2xl transition-all flex items-center justify-center flex-shrink-0 ${
              filterPanelOpen || selectedTagIds.length > 0 || selectedCity
                ? 'bg-white text-blue-600'
                : 'bg-white/15 backdrop-blur-sm text-white border border-white/10'
            }`}
            aria-label="Toggle filters"
          >
            <SlidersHorizontal className="w-5 h-5" />
            {(selectedTagIds.length > 0 || selectedCity) && (
              <span className="ml-1 text-xs font-bold">
                {selectedTagIds.length + (selectedCity ? 1 : 0)}
              </span>
            )}
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <PageLayout
      header={topHeader}
      stickyHeader
      noPadding
      onRefresh={async () => {
        await searchProducts({
          category_ids: selectedTagIds.length > 0 ? selectedTagIds.join(',') : undefined,
          attrs: Object.keys(selectedAttrs).length > 0 ? selectedAttrs : undefined,
          country_code: selectedCountry || undefined,
          city_name: selectedCity || undefined
        })
      }}
    >
      <div className="max-w-md mx-auto px-4 pt-3">
        {/* Active Filters Display */}
        {(selectedCity || activeFilterCount > 0) && (
          <div className="mb-4 flex flex-wrap gap-2">
            {selectedCity && (
              <button
                onClick={() => handleLocationChange(null, null)}
                className="inline-flex items-center bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs"
              >
                <MapPin className="w-3 h-3 mr-1" />
                {selectedCity}
                <X className="w-3 h-3 ml-1" />
              </button>
            )}
            {activeFilterCount > 0 && (
              <button
                onClick={() => handleApplyFilters([], {})}
                className="inline-flex items-center bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs"
              >
                <Filter className="w-3 h-3 mr-1" />
                {selectedTagIds.length > 0 && `${selectedTagIds.length} ${t('filter.categoriesLabel')}`}
                {selectedTagIds.length > 0 && Object.keys(selectedAttrs).length > 0 && ' · '}
                {Object.keys(selectedAttrs).length > 0 && `${Object.keys(selectedAttrs).length} ${t('filter.specsLabel')}`}
                <X className="w-3 h-3 ml-1" />
              </button>
            )}
            <button
              onClick={clearAllFilters}
              className="text-xs text-red-500 hover:text-red-600 px-2 py-1"
            >
              {t('filter.clearAll')}
            </button>
          </div>
        )}

        {/* Loading State */}
        {productsLoading && (
          <SkeletonGrid count={4} />
        )}

        {/* Error State */}
        {productsError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="text-red-600 text-sm">
              {productsError}
            </div>
            <button
              onClick={() => searchProducts({})}
              className="mt-2 text-red-600 text-sm underline"
            >
              Try again
            </button>
          </div>
        )}

        {/* Product Grid */}
        {!productsLoading && filteredProducts && filteredProducts.length > 0 && (
          <div className="grid grid-cols-2 gap-3">
            {filteredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

        {/* Empty State */}
        {!productsLoading && !productsError && (!products || products.length === 0) && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <div className="text-4xl mb-3">📦</div>
            <h2 className="text-lg font-bold text-gray-800 mb-2">
              No Products Found
            </h2>
            <p className="text-gray-500 text-sm mb-4">
              {selectedTagIds.length > 0 || selectedCity
                ? 'Try different filters'
                : 'No products available yet'}
            </p>
            {(selectedTagIds.length > 0 || selectedCity) && (
              <button
                onClick={clearAllFilters}
                className="bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
              >
                Clear Filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Filter Panel Modal */}
      <FilterPanel
        isOpen={filterPanelOpen}
        onClose={() => setFilterPanelOpen(false)}
        selectedTagIds={selectedTagIds}
        onApply={handleApplyFilters}
      />
    </PageLayout>
  )
}
